import type { ReactNode } from "react";

// Riel visual del switch (track + knob). aria-hidden: la semántica la aporta el
// <input> real que lo acompaña. Úsalo cuando ya existe un <label> + <input>
// propios (filas multi-selección o labels con contenido rico) y solo quieres
// cambiar el look del checkbox por un switch.
export function ToggleTrack({
  checked,
  disabled,
  className,
}: {
  checked: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
        checked ? "bg-accent-500" : "bg-ink-700"
      } ${disabled ? "opacity-50" : ""} ${className ?? ""}`}
    >
      <span
        className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-3.5" : "translate-x-0.5"
        }`}
      />
    </span>
  );
}

type ToggleProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  children?: ReactNode;
  /** Explicación en línea, chiquita y debajo del texto. Reemplaza al `title`
   *  (tooltip) que tenía antes: cero tooltips, la ayuda se ve sin pasar el
   *  mouse — en el celular el hover no existe. */
  hint?: ReactNode;
  disabled?: boolean;
  align?: "center" | "start";
  className?: string;
};

// Switch on/off accesible: un <label> con un checkbox REAL (sr-only) + el riel
// visual. Clic en cualquier parte (switch o texto) togglea, conserva la
// semántica de formulario, el foco por teclado y `disabled`. Usa `label` para
// texto simple o `children` para contenido rico.
export default function Toggle({
  checked,
  onChange,
  label,
  children,
  hint,
  disabled,
  align = "center",
  className,
}: ToggleProps) {
  const contenido = children ?? (label ? <span>{label}</span> : null);
  // Con hint, el switch se alinea con el primer renglón, no con el centro de
  // las dos líneas.
  const arriba = align === "start" || !!hint;
  return (
    <label
      className={`inline-flex ${arriba ? "items-start" : "items-center"} gap-2 text-xs text-zinc-300 select-none ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      } ${className ?? ""}`}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <ToggleTrack checked={checked} disabled={disabled} className={arriba ? "mt-0.5" : ""} />
      {hint ? (
        <span className="flex min-w-0 flex-col gap-0.5">
          {contenido}
          <span className="text-[11px] leading-snug text-zinc-500">{hint}</span>
        </span>
      ) : (
        contenido
      )}
    </label>
  );
}
